'use client';

import { useState, useEffect } from 'react';
import { userMessageService, UserMessage } from '@/lib/services/userMessageService';

interface MessagingCenterProps {
  profileId: string;
  onMessageRead: () => void;
}

export default function MessagingCenter({ profileId, onMessageRead }: MessagingCenterProps) {
  const [messages, setMessages] = useState<UserMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<UserMessage | null>(null);
  const [showCompose, setShowCompose] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    priority: 'normal',
  });

  useEffect(() => {
    loadMessages();
  }, [profileId]);

  const loadMessages = async () => {
    try {
      const data = await userMessageService.getMessagesByProfileId(profileId);
      setMessages(data);
    } catch (error: any) {
      console.error('Failed to load messages:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);

    try {
      await userMessageService.sendMessage({
        userProfileId: profileId,
        subject: formData.subject,
        message: formData.message,
        senderType: 'user',
        priority: formData.priority,
      });

      alert('Message sent successfully!');
      setFormData({ subject: '', message: '', priority: 'normal' });
      setShowCompose(false);
      loadMessages();
    } catch (error: any) {
      alert(`Failed to send message: ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  const handleViewMessage = async (message: UserMessage) => {
    setSelectedMessage(message);
    if (!message.isRead && message.senderType === 'admin') {
      try {
        await userMessageService.markAsRead(message.id!);
        onMessageRead();
        loadMessages();
      } catch (error: any) {
        console.error('Failed to mark message as read:', error.message);
      }
    }
  };

  const getPriorityBadge = (priority: string) => {
    const priorityConfig: Record<string, { bg: string; text: string }> = {
      low: { bg: 'bg-gray-100', text: 'text-gray-800' },
      normal: { bg: 'bg-blue-100', text: 'text-blue-800' },
      high: { bg: 'bg-orange-100', text: 'text-orange-800' },
      urgent: { bg: 'bg-red-100', text: 'text-red-800' },
    };

    const config = priorityConfig[priority] || priorityConfig.normal;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {priority.toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-12 h-12 border-4 border-brand-purple border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Compose Button */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-dark-navy">Messages</h2>
        <button
          onClick={() => setShowCompose(!showCompose)}
          className="px-6 py-3 bg-brand-purple text-white rounded-lg hover:bg-opacity-90 transition-colors"
        >
          {showCompose ? 'Cancel' : 'Compose Message'}
        </button>
      </div>

      {/* Compose Form */}
      {showCompose && (
        <div className="bg-white rounded-2xl shadow-card p-6">
          <h3 className="text-xl font-bold text-dark-navy mb-4">New Message</h3>
          <form onSubmit={handleSendMessage} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-dark-navy mb-2">Subject</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent"
                  placeholder="Message subject"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-navy mb-2">Priority</label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent"
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-navy mb-2">Message</label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent"
                placeholder="Type your message here..."
              />
            </div>

            <button
              type="submit"
              disabled={sending}
              className="px-6 py-3 bg-brand-purple text-white rounded-lg hover:bg-opacity-90 transition-colors disabled:opacity-50"
            >
              {sending ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      )}

      {/* Messages List */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Message List */}
        <div className="lg:col-span-1 bg-white rounded-2xl shadow-card p-6">
          <h3 className="text-lg font-bold text-dark-navy mb-4">Inbox</h3>
          {messages.length === 0 ? (
            <div className="text-center py-8">
              <svg className="w-12 h-12 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <p className="text-body-text text-sm">No messages yet.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {messages.map((msg) => (
                <button
                  key={msg.id}
                  onClick={() => handleViewMessage(msg)}
                  className={`w-full text-left p-3 rounded-lg transition-colors ${
                    selectedMessage?.id === msg.id
                      ? 'bg-brand-purple/10 border-2 border-brand-purple' :'hover:bg-gray-50 border-2 border-transparent'
                  } ${!msg.isRead && msg.senderType === 'admin' ? 'bg-blue-50' : ''}`}
                >
                  <div className="flex items-start justify-between mb-1">
                    <p className={`text-sm font-medium ${
                      !msg.isRead && msg.senderType === 'admin' ? 'text-brand-purple' : 'text-dark-navy'
                    }`}>
                      {msg.subject}
                    </p>
                    {!msg.isRead && msg.senderType === 'admin' && (
                      <span className="w-2 h-2 bg-brand-purple rounded-full"></span>
                    )}
                  </div>
                  <p className="text-xs text-body-text mb-1">
                    {msg.senderType === 'admin' ? 'From: Admin' : 'From: You'}
                  </p>
                  <p className="text-xs text-body-text">
                    {msg.createdAt ? new Date(msg.createdAt).toLocaleDateString() : 'N/A'}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Message Detail */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-card p-6">
          {selectedMessage ? (
            <div>
              <div className="border-b border-gray-200 pb-4 mb-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-xl font-bold text-dark-navy">{selectedMessage.subject}</h3>
                  {getPriorityBadge(selectedMessage.priority || 'normal')}
                </div>
                <div className="flex items-center space-x-4 text-sm text-body-text">
                  <span>
                    {selectedMessage.senderType === 'admin' ? 'From: Admin' : 'From: You'}
                  </span>
                  <span>
                    {selectedMessage.createdAt ? new Date(selectedMessage.createdAt).toLocaleString() : 'N/A'}
                  </span>
                </div>
              </div>
              <div className="prose max-w-none">
                <p className="text-dark-navy whitespace-pre-wrap">{selectedMessage.message}</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-center py-12">
              <div>
                <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <p className="text-body-text">Select a message to view</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}